import React, { useCallback, useEffect, useRef, useState } from "react";
import { trackEvent } from "../constants";
import dhyanaService from "../services/dhyanaService";
import { fetchPersonas } from "../services/configClient";
import ContentBlock from "./ContentBlock";
import EmailSignup from "./EmailSignup";

// ==================== HELPER FUNCTIONS ====================

// ==================== TYPES ====================
type PersonaKey = string;

type Classification = {
  persona: PersonaKey;
  name?: string;
  stages?: number[];
  confidence: number;
  source: string;
};

type StageInfo = {
  name: string;
  type: string;
};

// ==================== URL PARAMETER CLASSIFICATION ====================
const classifyFromURLParams = async (): Promise<Classification> => {
  if (typeof window === "undefined") {
    return { persona: "medium_balanced", confidence: 0, source: "ssr" };
  }

  // Use dhyanaService to classify based on URL parameters
  // The service now reads URL parameters and browser data internally
  try {
    const result = await dhyanaService.classifyPersona();
    return {
      persona: result.persona,
      confidence: result.confidence,
      source: result.source,
      stages: result.stages,
    };
  } catch (error) {
    console.error("Classification failed:", error);

    // Simple fallback classification based on URL params
    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get("utm_source") || "";
    const campaign = urlParams.get("utm_campaign") || "";
    const content = urlParams.get("utm_content") || "";

    if (source === "linkedin" && campaign.includes("executive")) {
      return {
        persona: "high_balanced",
        confidence: 85,
        source: "linkedin-executive",
        stages: [1, 3, 6, 9, 12],
      };
    }
    if (source === "facebook" && content.includes("technical")) {
      return {
        persona: "medium_technical",
        confidence: 80,
        source: "facebook-technical",
        stages: [2, 4, 8, 11, 14],
      };
    }
    if (source === "google" && campaign.includes("strategic")) {
      return {
        persona: "high_business",
        confidence: 75,
        source: "google-strategic",
        stages: [1, 2, 5, 8, 11],
      };
    }

    const p = urlParams.get("persona") as PersonaKey | null;
    if (p)
      return {
        persona: p,
        confidence: 90,
        source: "direct-param",
        stages: [1, 3, 5, 8, 14],
      };

    return {
      persona: "medium_balanced",
      confidence: 30,
      source: "anonymous",
      stages: [1, 2, 5, 7, 10, 13],
    };
  }
};

// ==================== MAIN ENDLESS SCROLL COMPONENT ====================
const DhyanaEndlessScroll: React.FC = () => {
  const [personaData, setPersonaData] = useState<Classification | null>(null);
  const [loadedBlocks, setLoadedBlocks] = useState<number[]>([]);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [stageConfig, setStageConfig] = useState<Record<string, StageInfo>>({});

  const visibleSetRef = useRef<Set<number>>(new Set());
  const loadingRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Load config and classify persona
  useEffect(() => {
    const init = async () => {
      try {
        // Load configuration from API
        const config = await dhyanaService.loadConfig();

        // Convert stages to the format we need
        if (config.stages) {
          const stages: Record<string, StageInfo> = {};
          Object.entries(config.stages).forEach(
            ([num, stage]: [string, any]) => {
              stages[num] = {
                name: stage.name || stage || `Stage ${num}`,
                type: stage.type || "discovery",
              };
            },
          );
          setStageConfig(stages);
        }

        // Classify persona with API call and defensive fallback
        let finalPersona;

        try {
          // Step 1: Get persona classification
          const server = await dhyanaService.classifyPersona();
          const personaKey =
            server?.persona?.key || server?.persona || "medium_balanced";

          // Step 2: Get full persona details from unified config
          const personas = await fetchPersonas();
          const personaConfig =
            personas[personaKey as keyof typeof personas] ||
            personas["medium_balanced"];

          finalPersona = {
            persona: personaKey,
            name: personaConfig.name || "Professional",
            stages: personaConfig.landing_stages || [1, 3, 5, 8, 14],
            confidence: server?.confidence ?? 70,
            source: server?.source || "server-classify",
          };
        } catch (err) {
          console.warn("Persona classification failed, using fallback:", err);
          // Defensive fallback: if 422 or any API error, use client-side classification
          const fallback = await classifyFromURLParams(); // client-side UTM heuristic

          // Try to enrich fallback with config data if available
          try {
            const personas = await fetchPersonas();
            const personaConfig =
              personas[fallback.persona as keyof typeof personas] ||
              personas["medium_balanced"];
            finalPersona = {
              ...fallback,
              name: personaConfig?.name || fallback.name || "Professional",
              stages: personaConfig?.landing_stages ||
                fallback.stages || [1, 3, 5, 8, 14],
            };
          } catch (configErr) {
            console.warn(
              "Config enrichment failed, using basic fallback:",
              configErr,
            );
            finalPersona = fallback;
          }
        }

        // Set persona data and initial blocks
        setPersonaData(finalPersona);
        const personaStages = finalPersona.stages || [1, 3, 5, 8, 14];
        const initial = personaStages.slice(0, 2);
        setLoadedBlocks(Array.from(new Set(initial)));

        trackEvent("persona_classified", {
          persona_type: finalPersona.persona,
          confidence: finalPersona.confidence,
          source: finalPersona.source,
          initial_stages: initial,
        });
      } catch (error) {
        console.error("Initialization failed:", error);
        // Set fallback data
        setPersonaData({
          persona: "medium_balanced",
          confidence: 30,
          source: "fallback",
          stages: [1, 2, 5, 7, 10, 13],
        });
        setLoadedBlocks([1, 2]);
      }
    };

    init();
  }, []);

  // Progress bar
  useEffect(() => {
    const onScroll = () => {
      const sh = document.documentElement.scrollHeight - window.innerHeight;
      const y = Math.max(
        0,
        Math.min(1, (window.pageYOffset || 0) / Math.max(1, sh)),
      );
      setScrollProgress(Math.round(y * 100));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Load more via sentinel
  const loadMore = useCallback(() => {
    if (!personaData || loadingRef.current) return;

    const personaStages = personaData.stages || [1, 3, 5, 8, 14];
    const remaining = personaStages.filter((s) => !loadedBlocks.includes(s));
    if (remaining.length === 0) return;

    loadingRef.current = true;
    setTimeout(() => {
      const next = remaining.slice(0, 2);
      setLoadedBlocks((prev) => Array.from(new Set([...prev, ...next])));
      loadingRef.current = false;
      trackEvent("more_content_loaded", {
        persona_type: personaData.persona,
        new_stages: next,
        total_loaded: loadedBlocks.length + next.length,
      });
    }, 400);
  }, [personaData, loadedBlocks]);

  useEffect(() => {
    if (!sentinelRef.current || typeof IntersectionObserver === "undefined")
      return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { root: null, threshold: 0, rootMargin: "1200px" },
    );
    io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, [loadMore]);

  // Stable visibility handler
  const handleBlockVisible = useCallback(
    (stageNumber: number) => {
      const set = visibleSetRef.current;
      if (!set.has(stageNumber)) {
        set.add(stageNumber);
        trackEvent("content_block_viewed", {
          stage_number: stageNumber,
          stage_name: stageConfig[stageNumber]?.name ?? `Stage ${stageNumber}`,
          persona_type: personaData?.persona,
          scroll_depth: scrollProgress,
        });
      }
    },
    [personaData, scrollProgress, stageConfig],
  );

  if (!personaData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="text-center">
          <div
            className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"
            aria-label="Loading"
          />
          <h2 className="text-2xl font-semibold text-slate-800 mb-2">
            Analyzing Your Profile
          </h2>
          <p className="text-slate-600">
            Optimizing content for your leadership level...
          </p>
        </div>
      </div>
    );
  }

  const personaName = personaData.name || "Professional";
  const personaStages = personaData.stages || [1, 3, 5, 8, 14];
  const allStagesLoaded = loadedBlocks.length >= personaStages.length;

  return (
    <div className="relative min-h-screen bg-gray-50">
      {/* Clean progress bar */}
      <div
        className="fixed top-0 left-0 right-0 h-1 bg-white z-50 shadow-sm"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={scrollProgress}
      >
        <div
          className="h-full bg-slate-900 transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Clean debug panel (development only) */}
      {process.env.NODE_ENV === "development" && (
        <div className="fixed bottom-4 right-4 bg-white border border-slate-200 p-3 rounded-lg text-xs z-40 shadow-lg">
          <div className="font-medium text-slate-900 mb-1">{personaName}</div>
          <div className="text-slate-600">
            Confidence: {personaData.confidence}%
          </div>
          <div className="text-slate-600">
            Blocks: {loadedBlocks.length}/{personaStages.length}
          </div>
        </div>
      )}

      {/* Content Blocks */}
      {loadedBlocks.map((stageNumber) => (
        <ContentBlock
          key={`stage-${stageNumber}`}
          stage={stageNumber}
          personaKey={personaData.persona}
          personaName={personaName}
          onVisible={handleBlockVisible}
          stageInfo={stageConfig[stageNumber]}
        />
      ))}

      {/* Clean final CTA */}
      {allStagesLoaded && (
        <section className="py-24 px-4 bg-white border-t border-slate-200">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-semibold text-slate-900 mb-4">
              Ready to get started?
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Join {personaName} leaders getting strategic insights
            </p>
            <EmailSignup persona={personaData.persona} />
            <p className="text-sm text-slate-500 mt-4">
              Personalized for {personaName} • Unsubscribe anytime
            </p>
          </div>
        </section>
      )}

      {/* Clean loading sentinel */}
      {!allStagesLoaded && (
        <>
          <div className="py-16 text-center bg-gray-50" aria-hidden>
            <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin mx-auto mb-3" />
            <p className="text-slate-500 text-sm">Loading more content...</p>
          </div>
          <div ref={sentinelRef} style={{ height: 1 }} />
        </>
      )}
    </div>
  );
};

export default DhyanaEndlessScroll;
